import { z } from 'zod';
import { equipmentKindSchema, slugSchema, unitKeySchema } from './common.js';
/**
 * Seed-file formats (D19). These are the *committed* outputs of the one-time
 * normalisation pipeline under `seed/`; the seed script loads them verbatim.
 */
export const seedIngredientLineSchema = z.object({
    /** Canonical ingredient name, singular, lowercase: "garlic", "ground chicken". */
    ingredient: z.string().min(1).max(120),
    /** Quantity in `unit`; null when the source gives none ("salt to taste"). */
    qty: z.number().positive().nullable(),
    /** Upper bound when the source gives a range ("12-13 cloves"). */
    qtyMax: z.number().positive().nullable().optional(),
    unit: unitKeySchema,
    /** Preparation note: "minced", "cut into 1/4 in strips". */
    prep: z.string().max(200).nullable().optional(),
    optional: z.boolean().default(false),
    /** Verbatim source line, for review and re-parsing. */
    raw: z.string().max(400),
});
export const seedEquipmentSchema = z.object({
    kind: equipmentKindSchema,
    count: z.number().int().positive().default(1),
    ovenTempF: z.number().int().min(150).max(600).optional(),
    ovenMode: z.enum(['bake', 'roast', 'broil', 'convection']).optional(),
});
export const seedStepSchema = z.object({
    text: z.string().min(1).max(2000),
    /** Active/passive duration of the step in minutes, if the source states or clearly implies one. */
    durationMin: z.number().positive().nullable(),
    /** True when the cook must stay with it (stirring); false for unattended (oven, simmer). */
    attended: z.boolean().default(true),
    /** Suggest a timer button for this step. */
    timer: z.boolean().default(false),
    canDoAhead: z.boolean().default(false),
    equipment: seedEquipmentSchema.optional(),
});
export const seedRecipeSchema = z.object({
    slug: slugSchema,
    title: z.string().min(1).max(200),
    description: z.string().max(1000).nullable(),
    servings: z.number().int().positive().nullable(),
    prepMin: z.number().int().nonnegative().nullable(),
    cookMin: z.number().int().nonnegative().nullable(),
    /** Minutes the dish rests before serving (timeline offset); omitted = 0. */
    restMin: z.number().int().nonnegative().max(1440).optional(),
    tags: z.array(z.string().min(1).max(40)).default([]),
    ingredients: z.array(seedIngredientLineSchema).min(1),
    steps: z.array(seedStepSchema).min(1),
    source: z.object({
        /** 'family' for the owner's own files; 'publix' / 'butcherbox' for scraped. */
        kind: z.enum(['family', 'publix', 'butcherbox', 'other']),
        /** Original file name or URL. */
        ref: z.string().min(1).max(500),
        siteName: z.string().max(100).nullable().optional(),
        author: z.string().max(100).nullable().optional(),
        licence: z.string().max(200),
        /** sha256 of the raw source at normalisation time. */
        sha256: z
            .string()
            .regex(/^[0-9a-f]{64}$/)
            .optional(),
    }),
    /** Free-text notes from the normaliser for the human reviewer (ambiguities, guesses). */
    reviewNotes: z.array(z.string()).default([]),
});
export const seedStoreSchema = z.object({
    slug: slugSchema,
    chain: z.string().min(1).max(80),
    name: z.string().min(1).max(120),
    address: z.string().max(300).nullable(),
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    radiusM: z.number().int().positive().default(150),
    /** D6: whole-store shelf-life adjustment in days (negative = shorter). */
    shelfLifeAdjustDays: z.number().int().default(0),
    shelfLifeNote: z.string().max(300).nullable().optional(),
});
export const seedManifestSchema = z.object({
    generatedAt: z.iso.datetime(),
    recipes: z.array(z.object({ slug: slugSchema, file: z.string(), sourceRef: z.string(), sha256: z.string() })),
});
/**
 * Unit-conversion reference values (`seed/units/ingredients.json`,
 * seed/units/README.md): approximate density and average whole-item weight
 * for common ingredients, loaded fill-if-null by apps/api/src/seed/units.ts.
 */
export const seedUnitIngredientSchema = z
    .object({
    /** Canonical-style ingredient name, lowercase: "all-purpose flour". */
    name: z.string().trim().min(1).max(120),
    /** Extra names tried, in order, when `name` matches no ingredient. */
    aliases: z.array(z.string().trim().min(1).max(120)).optional(),
    /** Grams per millilitre (water = 1); same bounds as `PATCH /ingredients/:id`. */
    densityGPerMl: z.number().positive().max(30).optional(),
    /** Average weight of one whole item, grams; same bounds as `PATCH /ingredients/:id`. */
    avgUnitWeightG: z.number().positive().max(10000).optional(),
    /** Where the figure comes from, generically ("USDA FoodData Central household measure (approx.)"). */
    source: z.string().min(1).max(200),
})
    .strict()
    .refine((v) => v.densityGPerMl !== undefined || v.avgUnitWeightG !== undefined, 'give densityGPerMl, avgUnitWeightG, or both');
export const seedUnitIngredientsSchema = z.array(seedUnitIngredientSchema);
//# sourceMappingURL=seed.js.map